import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './KebabMenu.css';

const SvgIcon = ({ children }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const MENU_ICONS = {
  kebab: (
    <SvgIcon>
      <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
    </SvgIcon>
  ),
  barcode: (
    <SvgIcon>
      <path d="M4 6v12M7 6v12M9 6v12M13 6v12M16 6v12M19 6v12" />
    </SvgIcon>
  ),
  topUp: (
    <SvgIcon>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </SvgIcon>
  ),
  history: (
    <SvgIcon>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </SvgIcon>
  ),
  edit: (
    <SvgIcon>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5z" />
    </SvgIcon>
  ),
  trash: (
    <SvgIcon>
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
    </SvgIcon>
  ),
  print: (
    <SvgIcon>
      <path d="M6 9V3h12v6" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="7" />
    </SvgIcon>
  ),
  whatsapp: (
    <SvgIcon>
      <path d="M21 11.5a8.4 8.4 0 0 1-12.5 7.3L3 21l2.3-5.4A8.4 8.4 0 1 1 21 11.5z" />
    </SvgIcon>
  ),
  view: (
    <SvgIcon>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </SvgIcon>
  ),
  returns: (
    <SvgIcon>
      <path d="M3 10h11a4 4 0 0 1 0 8H7" />
      <path d="M7 6l-4 4 4 4" />
    </SvgIcon>
  ),
  settle: (
    <SvgIcon>
      <path d="M20 6L9 17l-5-5" />
    </SvgIcon>
  )
};

const isKebabEvent = (event) => Boolean(
  event.target.closest('.kebab-menu') || event.target.closest('.kebab-menu-list')
);

export function useKebabMenu() {
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    if (!openId) return undefined;
    const close = (event) => {
      if (!isKebabEvent(event)) {
        setOpenId(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openId]);
  return [openId, setOpenId];
}

const KebabMenu = ({ menuId, openId, setOpenId, items, label = 'Actions' }) => {
  const open = openId === menuId;
  const visible = (items || []).filter(Boolean);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const placeMenu = () => {
    const btn = btnRef.current;
    const list = listRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuWidth = list?.offsetWidth || 200;
    const menuHeight = list?.offsetHeight || 0;
    let left = r.right - menuWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    let top = r.bottom + 4;
    if (menuHeight && top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, r.top - menuHeight - 4);
    }
    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    placeMenu();
    const onWin = () => placeMenu();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open, visible.length]);

  if (!visible.length) {
    return <span style={{ color: '#888' }}>—</span>;
  }

  return (
    <div className="kebab-menu">
      <button
        ref={btnRef}
        type="button"
        className="btn btn-icon btn-sm btn-secondary"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpenId(open ? null : menuId);
        }}
      >
        {MENU_ICONS.kebab}
      </button>
      {open && createPortal(
        <div
          ref={listRef}
          className="kebab-menu-list"
          role="menu"
          style={{ top: coords.top, left: coords.left }}
        >
          {visible.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`kebab-menu-item${item.danger ? ' danger' : ''}`}
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                setOpenId(null);
                item.onClick();
              }}
            >
              <span className="kebab-menu-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default KebabMenu;
